import { NavLink } from 'react-router-dom';
import { FC, JSX } from 'react';
import './ContentHeader.scss';
import { useTranslation } from 'react-i18next';

/**
 * @description Represents a navigation link in the content header
 * @typedef {Object} LinkType
 * @property {string} title - The title of the link (translation key)
 * @property {string} url - The URL the link points to
 */
type LinkType = {
  title: string;
  url: string;
};

/**
 * @description Props for the ContentHeader component
 * @typedef {Object} ContentHeaderProps
 * @property {string} [title] - Optional title to display in the header
 * @property {JSX.Element|undefined|null} [actions] - Optional action elements to display in the header
 * @property {LinkType[]} [links] - Optional array of navigation links
 */
type ContentHeaderProps = {
  title?: string;
  actions?: JSX.Element | undefined | null;
  links?: LinkType[];
};

/**
 * @description ContentHeader component that displays a title, actions, and navigation links.
 * Used throughout the application to provide consistent page headers with optional tabs.
 *
 * @SADtrace SAD-003-1.1, SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1, SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @component
 * @param {ContentHeaderProps} props - The component props
 * @param {string} [props.title] - Optional title to display
 * @param {JSX.Element|undefined|null} [props.actions] - Optional action elements
 * @param {LinkType[]} [props.links] - Optional array of navigation links
 *
 * @returns {JSX.Element} The rendered ContentHeader component
 */
const ContentHeader: FC<ContentHeaderProps> = ({ title, actions, links }) => {
  const { t } = useTranslation();

  return (
    <div className={'content-header'} data-testid="content-header">
      {!title && !actions ? null : (
        <div className="content-header__title">
          {title && <span>{title}</span>}
          <div className="content-header__actions">{!!actions && actions}</div>
        </div>
      )}

      {Array.isArray(links) && (
        <ul className="content-header__links">
          {links.map((link) => {
            return (
              <li key={link.url}>
                <NavLink className="content-header__link" to={link.url}>
                  {t(link.title)}
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ContentHeader;
